export type Spring = { stiffness: number; damping: number };

export const SETTLE: Spring = { stiffness: 320, damping: 32 };
export const GLIDE: Spring = { stiffness: 140, damping: 2 * Math.sqrt(140) };
export const CLOSE: Spring = { stiffness: 800, damping: 50 };

const SUBSTEPS = 4;

export function createSpringDriver<K extends string>(
    m: Record<K, number>,
    pixelKeys: readonly NoInfer<K>[],
    onFrame: () => void,
    reduceMotion: () => boolean,
) {
    type Values = Partial<Record<K, number>>;
    let frame: number | null = null;

    const stop = () => {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
    };

    const animate = (
        target: Values,
        spring: Spring,
        velocity: Values,
        onDone?: () => void,
    ) => {
        stop();
        const keys = Object.keys(target) as K[];
        const vel: Values = { ...velocity };
        let last = performance.now();
        const tick = (now: number) => {
            const dt = Math.max(0, Math.min(0.064, (now - last) / 1000));
            last = now;
            let settled = true;
            for (const key of keys) {
                const goal = target[key] ?? m[key];
                let x = m[key];
                let v = vel[key] ?? 0;
                for (let n = 0; n < SUBSTEPS; n++) {
                    const h = dt / SUBSTEPS;
                    const a =
                        -spring.stiffness * (x - goal) - spring.damping * v;
                    v += a * h;
                    x += v * h;
                }
                const eps = pixelKeys.includes(key) ? 0.25 : 0.0005;
                if (Math.abs(x - goal) > eps || Math.abs(v) > eps * 60) {
                    settled = false;
                    m[key] = x;
                    vel[key] = v;
                } else {
                    m[key] = goal;
                    vel[key] = 0;
                }
            }
            onFrame();
            if (settled) {
                frame = null;
                onDone?.();
                return;
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
    };

    const go = (
        target: Values,
        spring: Spring,
        velocity: Values,
        onDone?: () => void,
    ) => {
        if (!reduceMotion()) {
            animate(target, spring, velocity, onDone);
            return;
        }
        stop();
        Object.assign(m, target);
        onFrame();
        onDone?.();
    };

    return { animate, go, stop, running: () => frame !== null };
}
